import React from "react";
import {
  MdHourglassBottom,
  MdBatteryAlert,
} from "react-icons/md";

/**
 * Small visual pieces shared by every account notice: the urgency icon, a
 * highlighted date/number, and a minimal illustration. Pastel, flat, no
 * gradients — they sit on the tinted notice header.
 */

// One-off keyframes, injected once, for the gentle hourglass tilt.
let injected = false;
const ensureKeyframes = () => {
  if (injected || typeof document === "undefined") return;
  injected = true;
  const style = document.createElement("style");
  style.textContent =
    "@keyframes simpldTilt{0%,100%{transform:rotate(-12deg)}50%{transform:rotate(12deg)}}" +
    "@keyframes simpldFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}" +
    "@media (prefers-reduced-motion:reduce){.simpld-anim{animation:none!important}}";
  document.head.appendChild(style);
};

/** Hourglass for anything time-based, a low battery for credits. */
export const UrgencyIcon = ({ kind, color, size = 22, animate = true }) => {
  ensureKeyframes();
  const Icon = kind === "credits" ? MdBatteryAlert : MdHourglassBottom;
  return (
    <span
      className={animate ? "simpld-anim" : undefined}
      style={{
        display: "inline-flex",
        color,
        animation: animate ? "simpldTilt 2.4s ease-in-out infinite" : "none",
      }}
    >
      <Icon size={size} />
    </span>
  );
};

/** A date or number picked out in the notice's accent colour. */
export const Highlight = ({ children, color, bg }) => (
  <strong
    className="rounded-md px-1.5 py-0.5 font-bold"
    style={{ color, background: bg, whiteSpace: "nowrap" }}
  >
    {children}
  </strong>
);

/**
 * Renders `text`, wrapping every string in `highlights` in <Highlight>.
 * Plain strings in, nodes out, so notice copy stays easy to edit.
 */
export const HighlightedText = ({ text, highlights = [], color, bg }) => {
  const marks = highlights.filter(Boolean);
  if (marks.length === 0) return <>{text}</>;
  const pattern = new RegExp(
    `(${marks.map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "g"
  );
  return (
    <>
      {text.split(pattern).map((part, i) =>
        marks.includes(part) ? (
          <Highlight key={i} color={color} bg={bg}>
            {part}
          </Highlight>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

/** Calendar with a clock on it: renewals, trials, grace period. */
const CalendarClock = ({ accent, tint }) => (
  <svg viewBox="0 0 160 130" width="100%" height="100%" aria-hidden="true">
    <ellipse cx="80" cy="120" rx="52" ry="6" fill={accent} opacity="0.1" />
    <rect x="22" y="22" width="92" height="84" rx="12" fill="#fff" />
    <rect x="22" y="22" width="92" height="24" rx="12" fill={tint} />
    <rect x="22" y="36" width="92" height="10" fill={tint} />
    <rect x="44" y="12" width="7" height="20" rx="3.5" fill={accent} opacity="0.85" />
    <rect x="85" y="12" width="7" height="20" rx="3.5" fill={accent} opacity="0.85" />
    {[0, 1, 2, 3].map((c) =>
      [0, 1].map((r) => (
        <rect
          key={`${c}-${r}`}
          x={34 + c * 19}
          y={58 + r * 18}
          width="11"
          height="9"
          rx="3"
          fill={accent}
          opacity={c === 2 && r === 1 ? 0.9 : 0.18}
        />
      ))
    )}
    <circle cx="112" cy="94" r="26" fill="#fff" />
    <circle cx="112" cy="94" r="21" fill={tint} />
    <circle cx="112" cy="94" r="21" fill="none" stroke={accent} strokeWidth="3.5" />
    <line x1="112" y1="94" x2="112" y2="80" stroke={accent} strokeWidth="3.5" strokeLinecap="round" />
    <line x1="112" y1="94" x2="123" y2="100" stroke={accent} strokeWidth="3.5" strokeLinecap="round" />
    <circle cx="112" cy="94" r="3" fill={accent} />
  </svg>
);

/** Message bubble over a nearly empty meter: credits running out. */
const MessageMeter = ({ accent, tint }) => (
  <svg viewBox="0 0 160 130" width="100%" height="100%" aria-hidden="true">
    <ellipse cx="80" cy="120" rx="52" ry="6" fill={accent} opacity="0.1" />
    <path
      d="M30 20h82a12 12 0 0 1 12 12v40a12 12 0 0 1-12 12H62l-18 16V84H30a12 12 0 0 1-12-12V32a12 12 0 0 1 12-12z"
      fill="#fff"
    />
    <rect x="34" y="36" width="64" height="7" rx="3.5" fill={accent} opacity="0.25" />
    <rect x="34" y="51" width="82" height="7" rx="3.5" fill={accent} opacity="0.18" />
    <rect x="34" y="66" width="46" height="7" rx="3.5" fill={accent} opacity="0.12" />
    <rect x="86" y="86" width="58" height="26" rx="7" fill="#fff" />
    <rect x="91" y="91" width="48" height="16" rx="4" fill={tint} />
    <rect x="91" y="91" width="13" height="16" rx="4" fill={accent} />
    <rect x="144" y="94" width="5" height="10" rx="2" fill={accent} opacity="0.6" />
  </svg>
);

export const Illustration = ({ kind, accent, tint }) => {
  ensureKeyframes();
  const Art = kind === "credits" ? MessageMeter : CalendarClock;
  return (
    <div
      className="simpld-anim"
      style={{ animation: "simpldFloat 4s ease-in-out infinite" }}
    >
      <Art accent={accent} tint={tint} />
    </div>
  );
};
